import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, Eyebrow } from "@/components/Motion";
import { imgUrl } from "@/lib/api";
import { useSiteMedia } from "@/context/SiteMediaContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSeo } from "@/lib/useSeo";

const IMG_STUDIO = "/gallery/placeholder.jpg";
const IMG_WATER = "/gallery/photo4.webp";
const IMG_CHILD = "/gallery/photo1.jpeg";

export default function Home() {
  const { m } = useSiteMedia();
  const { c } = useSiteContent();
  useSeo(c("seo_home_title"), c("seo_home_desc"));
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden bg-ivory flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-ivory to-ivory" />
        <div className="relative z-10 w-full max-w-[1100px] mx-auto px-6 md:px-10 pt-28 pb-16 flex flex-col items-center text-center">
          <motion.img
            src="/brainwaves-logo-transparent.png"
            alt="Brainwaves — The Path to Creative Intelligence"
            className="w-[280px] sm:w-[420px] md:w-[560px] lg:w-[640px] h-auto object-contain"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          />
          {c("home_hero_statement") && (
            <motion.h1
              className="font-serif text-matte text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.3] max-w-3xl mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="home-hero-statement"
            >
              {c("home_hero_statement")}
            </motion.h1>
          )}
          {c("home_hero_cta_label") && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10"
            >
              <Link
                to={c("home_hero_cta_link", "/shop")}
                data-testid="hero-explore-btn"
                className="group inline-flex items-center gap-3 bg-matte text-ivory px-10 py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
              >
                {c("home_hero_cta_label", "Explore")}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* STATEMENT */}
      {(c("home_belief_label") || c("home_belief_text")) && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-2">
              {c("home_belief_label") && <Eyebrow>{c("home_belief_label")}</Eyebrow>}
            </div>
            <FadeUp className="md:col-span-9">
              <p className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.25] text-matte">
                {c("home_belief_text")}
              </p>
            </FadeUp>
          </div>
        </section>
      )}

      {/* EDITORIAL GRID */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-8">
          <FadeUp className="md:col-span-7">
            <Link to={c("home_studio_link", "/studio")} className="group block" data-testid="feature-studio">
              <div className="overflow-hidden">
                <img
                  src={imgUrl(m("home_studio_tile", IMG_STUDIO))}
                  alt={c("home_studio_title", "Studio")}
                  className="w-full h-[52vh] md:h-[64vh] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                />
              </div>
              <div className="pt-6 flex items-end justify-between">
                <div>
                  {c("home_studio_eyebrow") && <Eyebrow>{c("home_studio_eyebrow")}</Eyebrow>}
                  <h3 className="font-serif text-3xl mt-2">{c("home_studio_title")}</h3>
                </div>
                <ArrowRight className="text-plum group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </Link>
          </FadeUp>

          <FadeUp delay={0.15} className="md:col-span-5 md:mt-24">
            <Link to={c("home_programs_link", "/programs")} className="group block" data-testid="feature-programs">
              <div className="overflow-hidden">
                <img
                  src={imgUrl(m("home_programs_tile", IMG_CHILD))}
                  alt={c("home_programs_title", "Programs")}
                  className="w-full h-[52vh] md:h-[64vh] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                />
              </div>
              <div className="pt-6 flex items-end justify-between">
                <div>
                  {c("home_programs_eyebrow") && <Eyebrow>{c("home_programs_eyebrow")}</Eyebrow>}
                  <h3 className="font-serif text-3xl mt-2">{c("home_programs_title")}</h3>
                </div>
                <ArrowRight className="text-plum group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <FadeUp>
          <Eyebrow>{c("home_pillars_eyebrow")}</Eyebrow>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-x-10 gap-y-16 mt-14">
          {[1, 2, 3, 4, 5, 6].map((i) => {
            const title = c(`home_pillar_${i}_title`);
            if (!title) return null;
            return (
              <FadeUp key={i} delay={(i - 1) * 0.06}>
                <div className="border-t border-matte/15 pt-6">
                  <h3 className="font-serif text-3xl mt-3 mb-3">{title}</h3>
                  <p className="text-matte/60 leading-relaxed text-sm">{c(`home_pillar_${i}_body`)}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* SHOP CTA */}
      <section className="relative">
        <div className="grid md:grid-cols-2">
          <div className="bg-cream flex items-center">
            <FadeUp className="px-6 md:px-20 py-24 md:py-40">
              {c("home_shop_eyebrow") && <Eyebrow>{c("home_shop_eyebrow")}</Eyebrow>}
              <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 mb-6 leading-tight">
                {c("home_shop_title")}
              </h2>
              <p className="text-matte/60 max-w-md leading-relaxed mb-8">
                {c("home_shop_text")}
              </p>
              {c("home_shop_cta_label") && (
                <Link
                  to={c("home_shop_cta_link", "/shop/all")}
                  data-testid="home-shop-btn"
                  className="group inline-flex items-center gap-3 border border-matte px-8 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors duration-300"
                >
                  {c("home_shop_cta_label", "Enter the Shop")}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              )}
            </FadeUp>
          </div>
          <div className="overflow-hidden h-[50vh] md:h-auto">
            <img src={imgUrl(m("home_shop_cta", IMG_WATER))} alt="Artwork" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
